<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import {
  parseTaskFromMessage,
  updateTaskStatus,
} from 'dashboard/helper/tasksHelper';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TaskItem from './TaskItem.vue';
import TaskFormModal from './TaskFormModal.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const alert = useAlert();

const tasks = ref([]);
const isLoading = ref(false);
const showCreateModal = ref(false);
const editingTask = ref(null);
const activeFilter = ref('all');

const currentUser = useMapGetter('getCurrentUser');

const filterOptions = computed(() => [
  { key: 'all', label: t('TASKS.FILTER.ALL'), count: tasks.value.length },
  {
    key: 'pending',
    label: t('TASKS.FILTER.PENDING'),
    count: tasks.value.filter(
      task => !task.isCompleted && task.status !== 'En Proceso'
    ).length,
  },
  {
    key: 'in_progress',
    label: t('TASKS.STATUS.IN_PROGRESS'),
    count: tasks.value.filter(task => task.status === 'En Proceso').length,
  },
  {
    key: 'completed',
    label: t('TASKS.FILTER.COMPLETED'),
    count: tasks.value.filter(task => task.isCompleted).length,
  },
]);

const filteredTasks = computed(() => {
  if (activeFilter.value === 'all') return tasks.value;
  if (activeFilter.value === 'pending')
    return tasks.value.filter(
      task => !task.isCompleted && task.status !== 'En Proceso'
    );
  if (activeFilter.value === 'in_progress')
    return tasks.value.filter(task => task.status === 'En Proceso');
  if (activeFilter.value === 'completed')
    return tasks.value.filter(task => task.isCompleted);
  return tasks.value;
});

const pendingTasksCount = computed(() => {
  return tasks.value.filter(task => !task.isCompleted).length;
});

const loadTasks = async () => {
  isLoading.value = true;
  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId: props.conversationId,
    });

    if (response && response.data && response.data.payload) {
      tasks.value = response.data.payload
        .filter(msg => msg.private && msg.content?.startsWith('[TAREA]'))
        .map(msg => parseTaskFromMessage(msg))
        .filter(Boolean)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  } catch (error) {
    alert.error(t('TASKS.ERROR_LOADING'));
  } finally {
    isLoading.value = false;
  }
};

const handleCreateTask = () => {
  editingTask.value = null;
  showCreateModal.value = true;
};

const handleEditTask = task => {
  editingTask.value = task;
  showCreateModal.value = true;
};

const handleDeleteTask = async task => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TASKS.DELETE_CONFIRM'))) return;

  try {
    await MessageApi.delete(props.conversationId, task.id);
    await loadTasks();
  } catch (error) {
    alert.error(t('TASKS.ERROR_DELETING'));
  }
};

const handleStatusChange = async (task, newStatus) => {
  try {
    await updateTaskStatus(task, newStatus, props.conversationId);
    await loadTasks();
  } catch (error) {
    alert.error(t('TASKS.ERROR_UPDATING'));
  }
};

const handleSaveTask = async taskData => {
  try {
    let emoji = '⏸️';
    if (taskData.status === 'Completada') emoji = '✅';
    else if (taskData.status === 'En Proceso') emoji = '🔄';

    const taskMessage = `[TAREA] ${taskData.title}
📋 Descripción: ${taskData.description || 'Sin descripción'}
📅 Vence: ${taskData.dueDate}
👤 Asignado: @${taskData.assignedTo || currentUser.value.name}
${emoji} Estado: ${taskData.status}`;

    // Si se está editando, eliminar el mensaje anterior primero
    if (editingTask.value) {
      await MessageApi.delete(props.conversationId, editingTask.value.id);
    }

    await MessageApi.create({
      conversationId: props.conversationId,
      message: taskMessage,
      private: true,
    });

    editingTask.value = null;
    showCreateModal.value = false;
    await loadTasks();
  } catch (error) {
    alert.error(t('TASKS.ERROR_SAVING'));
  }
};

onMounted(() => {
  loadTasks();
});

watch(
  () => props.conversationId,
  () => {
    activeFilter.value = 'all';
    loadTasks();
  }
);
</script>

<template>
  <div>
    <!-- Header con contador y botón crear -->
    <div class="flex items-center justify-between p-2 pb-1">
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ $t('TASKS.PENDING_COUNT') }}
        </span>
        <span
          v-if="pendingTasksCount > 0"
          class="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-red-500 rounded-full"
        >
          {{ pendingTasksCount }}
        </span>
      </div>
      <NextButton
        faded
        xs
        icon="i-lucide-plus"
        :label="$t('TASKS.CREATE')"
        @click="handleCreateTask"
      />
    </div>

    <!-- Filtros por estado -->
    <div
      v-if="!isLoading && tasks.length > 0"
      class="flex items-center gap-1 px-2 pb-2 flex-wrap"
    >
      <button
        v-for="opt in filterOptions"
        :key="opt.key"
        class="px-2 py-0.5 text-xs font-medium rounded-full transition-colors"
        :class="[
          activeFilter === opt.key
            ? 'bg-n-teal-9 text-white'
            : 'bg-n-slate-3 dark:bg-n-slate-10 text-n-slate-11 hover:bg-n-slate-4 dark:hover:bg-n-slate-9',
        ]"
        @click="activeFilter = opt.key"
      >
        {{ opt.label }}
        <span class="ml-1 opacity-70">{{ opt.count }}</span>
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center gap-2 justify-center p-6 text-n-slate-12"
    >
      <span class="text-sm">{{ $t('TASKS.LOADING') }}</span>
      <Spinner class="size-5" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!tasks.length"
      class="p-3 text-center text-sm text-n-slate-11"
    >
      {{ $t('TASKS.NO_TASKS') }}
    </div>

    <!-- Empty filtered state -->
    <div
      v-else-if="!filteredTasks.length"
      class="p-3 text-center text-sm text-n-slate-11"
    >
      {{ $t('TASKS.NO_TASKS_IN_COLUMN') }}
    </div>

    <!-- Lista de tareas -->
    <div v-else class="p-1 space-y-2">
      <TaskItem
        v-for="task in filteredTasks"
        :key="task.id"
        :task="task"
        @edit="handleEditTask"
        @delete="handleDeleteTask"
        @status-change="handleStatusChange"
      />
    </div>

    <!-- Modal de crear/editar -->
    <TaskFormModal
      v-if="showCreateModal"
      :task="editingTask"
      @save="handleSaveTask"
      @close="showCreateModal = false"
    />
  </div>
</template>
